import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Plus } from 'lucide-react';
import { CalendarView } from '../components/calendar/CalendarView';
import { WeekView } from '../components/calendar/WeekView';
import { PostPreview } from '../components/calendar/PostPreview';
import { ScheduleModal } from '../components/calendar/ScheduleModal';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { PostStatusDropdown, type PostStatus } from '../components/common/PostStatusDropdown';
import { SocialIcon } from '../components/icons/SocialIcon';
import { useCalendarUrlState } from '../store/calendarStore';
import type { Post } from '../types/calendar';
import type { Brand } from '../types';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { usePosts } from '../hooks/usePosts';
import { useUpdatePost } from '../hooks/useUpdatePost';
import { CreatePostModal } from '../components/posts/CreatePostModal';
import { ErrorHandlingMedia } from '../components/common/ErrorHandlingMedia';
import { MediaFormatDropdown, type MediaFormat } from '../components/common/MediaFormatDropdown';
import { PostFormatDropdown, type PostFormat } from '../components/common/PostFormatDropdown';
import { useToast } from '../context/ToastContext';
import { normalizeMedia } from '../utils/mediaUtils';
import { MediaStructureDebug } from '../components/debug/MediaStructureDebug';
import { useQueryClient } from '@tanstack/react-query';

const Calendar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [calendarViewType, setCalendarViewType] = React.useState<'month' | 'week'>('month');
  
  // New filter states
  const [mediaFormat, setMediaFormat] = React.useState<MediaFormat>('all');
  const [postFormat, setPostFormat] = React.useState<PostFormat>('all');
  
  // States for draft cards hover preview
  const [hoveredDraftPost, setHoveredDraftPost] = React.useState<string | null>(null);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  
  // States for drag and drop scheduling
  const [scheduleModalOpen, setScheduleModalOpen] = React.useState(false);
  const [draggedPost, setDraggedPost] = React.useState<Post | null>(null);
  const [dropDate, setDropDate] = React.useState<Date | null>(null);
  const [dragOverDate, setDragOverDate] = React.useState<Date | null>(null);
  
  // State for media debug mode
  const [debugMode, setDebugMode] = React.useState(false);

  const {
    view,
    currentDate, 
    setCurrentDate,
    selectedBrand, 
    setSelectedBrand,
    statusFilter,
    setStatusFilter
  } = useCalendarUrlState();

  const { data: postsData, isLoading: isLoadingPosts, error: postsError } = usePosts();
  const posts = postsData?.posts || [];
  const { showToast } = useToast();
  const updatePostMutation = useUpdatePost();

  // Invalidate posts queries when user returns to the page
  React.useEffect(() => {
    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [queryClient]);

  // Add brand extraction logic back
  const brands: Brand[] = [
    { id: 'all', name: t('brandDropdown.allBrands', 'All Brands'), logo: '/logo.svg' },
    ...Array.from(new Set(posts.map(post => post.brandId))).map(brandId => {
      const post = posts.find(p => p.brandId === brandId)!;
      // Handle cases where post might be undefined if posts array is empty or brandId doesn't match
      if (!post) {
        return { id: brandId || 'unknown', name: 'Unknown Brand', logo: '' }; 
      }
      return {
        id: brandId || 'unknown',
        name: post.brandName,
        logo: post.brandLogo
      };
    })
  ];

  // Helper function to determine media format of a post
  const getPostMediaFormat = (post: Post): MediaFormat => {
    const normalized = normalizeMedia(post.media);
    if (!normalized) return 'text';
    if (normalized.type === 'image') return 'image';
    if (normalized.type === 'video') return 'video';
    // TODO: Add carousel detection logic when supported
    return 'text';
  };

  // Helper function to get post format
  const getPostFormat = (post: Post): PostFormat => {
    return post.format === 'reel' ? 'reel' : 'regular';
  };

  // Filter posts for CalendarView (uses selectedBrand, statusFilter, mediaFormat, postFormat)
  const filteredPosts = posts.filter(post => {
    // Exclude drafts from calendar - they'll go in the sidebar
    if (post.status === 'draft') return false;
    if (selectedBrand !== 'all' && post.brandId !== selectedBrand) return false;
    if (statusFilter !== 'all' && post.status !== statusFilter) return false;
    if (mediaFormat !== 'all' && getPostMediaFormat(post) !== mediaFormat) return false;
    if (postFormat !== 'all' && getPostFormat(post) !== postFormat) return false;
    return true;
  });

  // Get draft posts for sidebar (also apply filters except status)
  const draftPosts = posts.filter(post => {
    if (post.status !== 'draft') return false;
    if (selectedBrand !== 'all' && post.brandId !== selectedBrand) return false;
    if (mediaFormat !== 'all' && getPostMediaFormat(post) !== mediaFormat) return false;
    if (postFormat !== 'all' && getPostFormat(post) !== postFormat) return false;
    return true;
  });

  const handlePostClick = (post: Post) => {
    // Scroll to top of page for better mobile experience
    window.scrollTo(0, 0);
    navigate(`/posts/${post.id}/edit`);
  };
  
  const handleCreatePost = () => {
    navigate('/posts/new');
  };

  // Handlers for the CreatePostModal
  const handleCreateWithAI = () => {
    setIsCreateModalOpen(false);
    navigate('/content-creation');
  };

  const handleCreateManually = () => {
    setIsCreateModalOpen(false);
    navigate('/posts/new');
  };

  const handleDragStart = (e: React.DragEvent, post: Post) => {
    setDraggedPost(post);
    
    // Create a custom drag image that shows the full card
    const dragElement = e.currentTarget as HTMLElement;
    const rect = dragElement.getBoundingClientRect();
    
    // Clone the element to create a clean drag image
    const clone = dragElement.cloneNode(true) as HTMLElement;
    clone.style.width = rect.width + 'px';
    clone.style.height = rect.height + 'px';
    clone.style.position = 'absolute';
    clone.style.top = '-1000px';
    clone.style.left = '-1000px';
    clone.style.pointerEvents = 'none';
    clone.style.transform = 'none';
    clone.style.opacity = '0.9';
    clone.style.backgroundColor = 'white';
    clone.style.borderRadius = '8px';
    clone.style.border = '1px solid black';
    clone.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
    
    // Add to body temporarily
    document.body.appendChild(clone);
    
    // Set as drag image
    e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2);
    
    // Remove after a short delay
    setTimeout(() => {
      if (document.body.contains(clone)) {
        document.body.removeChild(clone);
      }
    }, 0);
  };

  const handleDrop = (date: Date) => {
    if (draggedPost) {
      console.log('📅 handleDrop - Dropped on date:', date, 'ISO:', date.toISOString());
      setDropDate(date);
      setScheduleModalOpen(true);
    }
  };

  const handleScheduleConfirm = async (scheduledAtUtc: string | null, timezone?: string) => {
    if (draggedPost && scheduledAtUtc) {
      try {
        console.log('📅 handleScheduleConfirm - Received scheduledAtUtc:', scheduledAtUtc, 'timezone:', timezone);
        console.log('📅 handleScheduleConfirm - Original dropDate was:', dropDate, 'ISO:', dropDate?.toISOString());
        
        // Update the post with the new schedule
        const updatePayload = {
          ...draggedPost,
          status: 'scheduled',
          scheduledAt: scheduledAtUtc
        };
        
        await updatePostMutation.mutateAsync({
          postId: draggedPost.id,
          postData: updatePayload,
          isFormData: false
        });
        
        // Show success message
        showToast(
          t('calendar.scheduleSuccess', 'Post scheduled successfully!'),
          'success'
        );
        
        // Reset states
        setDraggedPost(null);
        setDropDate(null);
        setScheduleModalOpen(false);
      } catch (error) {
        console.error('Error scheduling post:', error);
        showToast(
          t('calendar.scheduleError', 'Failed to schedule post. Please try again.'),
          'error'
        );
      }
    }
  };

  const handleScheduleCancel = () => {
    setDraggedPost(null);
    setDropDate(null);
    setScheduleModalOpen(false);
  };

  const getOptimalPosition = () => {
    // Dynamically size the preview card so it never exceeds 90% of the viewport on mobile
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const cardWidth = Math.min(800, viewportWidth * 0.9);
    const cardHeight = Math.min(500, viewportHeight * 0.8);
    const margin = 20; // Margin from screen edges

    let x = mousePosition.x + 10; // Default: to the right of cursor
    let y = mousePosition.y - 100; // Default: above cursor

    // Prevent right overflow
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }

    // Prevent left overflow
    if (x < margin) {
      x = margin;
    }

    // Prevent bottom overflow
    if (y + cardHeight + margin > viewportHeight) {
      y = viewportHeight - cardHeight - margin;
    }

    // Prevent top overflow
    if (y < margin) {
      y = margin;
    }

    return { x, y };
  };

  // Loading state
  if (isLoadingPosts) {
    // Skeleton Loader for Calendar Page
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 animate-pulse overflow-x-hidden">
        <div className="w-full max-w-[95%] mx-auto px-2 sm:px-4 py-8">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-24 sm:w-32"></div>
          </div>
          {/* Filters Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1"></div> {/* Spacer */} 
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-full sm:w-32 lg:w-40"></div>
              <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-full sm:w-32 lg:w-40"></div>
            </div>
          </div>
          {/* CalendarView Skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-2 sm:p-4 lg:p-6 max-w-full overflow-hidden">
            {/* Calendar Header Skeleton */}
            <div className="flex justify-between items-center mb-4">
              <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gray-200 rounded-full"></div>
              <div className="h-5 sm:h-6 bg-gray-200 rounded w-1/3"></div>
              <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gray-200 rounded-full"></div>
            </div>
            {/* Calendar Grid Skeleton */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center text-xs font-semibold text-gray-500 mb-2">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px border-t border-l overflow-hidden">
              {[...Array(35)].map((_, i) => (
                <div key={i} className="relative bg-gray-50 border-r border-b h-16 sm:h-24 lg:h-32">
                  <div className="absolute top-0.5 left-0.5 sm:top-1 sm:left-1 h-3 w-3 sm:h-4 sm:w-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Note: We removed the error state blocking return to always show the calendar
  // Even if posts fail to load, users can still view and use the calendar interface

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 overflow-x-hidden">
      <div className="w-full max-w-[100vw] mx-auto px-2 sm:px-4 overflow-x-hidden">
        <div className="py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-title mb-2">
                {t('calendar.title')}
              </h1>
              <p className="mt-1 text-sm sm:text-base text-gray-500 font-body">
                {t('calendar.subtitle')}
              </p>
              {postsError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <p className="text-sm text-red-600">
                    ⚠️ {t('calendar.errorLoadingPosts', 'Unable to load some posts. The calendar may not show all scheduled content.')}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setDebugMode(!debugMode)}
                className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors text-sm ${debugMode ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                title="Toggle Media Debug Mode"
              >
                🔧 {debugMode ? 'Debug ON' : 'Debug'}
              </button>
              <button 
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                {t('sidebar.createContent')}
              </button>
            </div>
          </div>

          {/* Add Filters Section Back */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 overflow-x-auto">
             <div className="flex items-center gap-2">
               {/* Calendar View Type Toggles */}
               <button
                 onClick={() => setCalendarViewType('month')}
                 className={`inline-flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-sm ${calendarViewType === 'month' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
               >
                 <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                 Mois
               </button>
               <button
                 onClick={() => setCalendarViewType('week')}
                 className={`inline-flex items-center px-2 sm:px-3 py-1.5 rounded-lg text-sm ${calendarViewType === 'week' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
               >
                 <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                 Semaine
               </button>
             </div>
             <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
               <PostStatusDropdown value={statusFilter} onChange={setStatusFilter} />
               <MediaFormatDropdown value={mediaFormat} onChange={setMediaFormat} />
               <PostFormatDropdown value={postFormat} onChange={setPostFormat} />
               <BrandDropdown brands={brands} selectedBrandId={selectedBrand} onBrandSelect={setSelectedBrand} />
             </div>
          </div>

          {/* CalendarView */}
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Main Calendar */}
            <div className="flex-1 min-w-0">
              {calendarViewType === 'month' ? (
                <CalendarView
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  posts={filteredPosts}
                  onPostClick={handlePostClick}
                  onDrop={handleDrop}
                  dragOverDate={dragOverDate}
                  onDragOver={setDragOverDate}
                  onPostDragStart={handleDragStart}
                />
              ) : (
                <WeekView
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  posts={filteredPosts}
                  onPostClick={handlePostClick}
                  onDrop={handleDrop}
                  dragOverDate={dragOverDate}
                  onDragOver={setDragOverDate}
                  onPostDragStart={handleDragStart}
                />
              )}
            </div>

            {/* Drafts Sidebar */}
            <div className="w-full lg:w-80 bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4 mt-4 lg:mt-0">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {draftPosts.length}
                </span>
                <h3 className="font-semibold text-gray-900">Non planifiés</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Glissez et déposez ces brouillons sur le calendrier pour les programmer.
              </p>
              
              {draftPosts.length > 0 ? (
                <div className="space-y-3">
                  {draftPosts.map((post) => (
                    <div
                      key={post.id}
                      draggable
                      onClick={() => handlePostClick(post)}
                      className="cursor-move p-3 border border-black rounded-lg hover:bg-gray-50 transition-colors"
                      onDragStart={(e) => handleDragStart(e, post)}
                      onMouseEnter={(e) => {
                        setHoveredDraftPost(post.id);
                        setMousePosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => {
                        if (hoveredDraftPost === post.id) {
                          setMousePosition({ x: e.clientX, y: e.clientY });
                        }
                      }}
                      onMouseLeave={() => setHoveredDraftPost(null)}
                    >
                      <div className="flex items-start gap-3">
                        {(() => {
                          const normalizedMedia = normalizeMedia(post.media);
                          return normalizedMedia && (
                            <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded overflow-visible relative">
                              <ErrorHandlingMedia
                                src={normalizedMedia.url} 
                                alt="Post media"
                                type={normalizedMedia.type}
                                className="w-full h-full object-cover"
                                controls={false}
                              />
                              {/* Media format emoji in bottom right corner */}
                              <div className="absolute bottom-0 right-0 bg-white text-gray-800 text-xs px-1 py-0.5 rounded-full flex items-center transform translate-x-1/2 translate-y-1/2 shadow-sm border border-gray-200">
                                <span className="text-[10px]">{getPostMediaFormat(post) === 'video' ? '🎬' : '🖼️'}</span>
                              </div>
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-2">
                            <p className="text-sm text-gray-900 line-clamp-2 flex-1">
                              {post.content}
                            </p>
                            {!post.media && (
                              <span className="text-[10px] bg-gray-100 px-1 py-0.5 rounded flex-shrink-0">📝</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {post.brandLogo && (
                              <img 
                                src={post.brandLogo} 
                                alt={post.brandName || 'Brand logo'}
                                className="w-4 h-4 rounded object-cover"
                              />
                            )}
                            <div className="flex gap-1">
                              {post.platforms.map((platform: string) => (
                                <SocialIcon 
                                  key={platform}
                                  platform={platform as SocialPlatform}
                                  size={14}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Debug Mode - Show Media Structure */}
                      {debugMode && (
                        <div className="mt-2">
                          <MediaStructureDebug 
                            media={post.media} 
                            postId={post.id}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">📝</div>
                  <p className="text-sm text-gray-500">Aucun brouillon en attente</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PostPreview for draft hover */}
      {hoveredDraftPost && (
        <div 
          className="fixed pointer-events-none z-50"
          style={{
            left: getOptimalPosition().x,
            top: getOptimalPosition().y,
          }}
        >
          <PostPreview
            post={draftPosts.find((p) => p.id === hoveredDraftPost)!}
          />
        </div>
      )}

      {/* Render the modals */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
      />
      
      <ScheduleModal
        isOpen={scheduleModalOpen}
        onClose={handleScheduleCancel}
        onConfirm={handleScheduleConfirm}
        post={draggedPost}
        initialDate={dropDate || undefined}
      />
    </div>
  );
};

export default Calendar;